import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Paper,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Popover,
  TextField,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Autocomplete,
  InputLabel,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import SettingsIcon from "@mui/icons-material/Settings";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import "./OneDriveExcel.css";

const POLLING_INTERVAL_MS = 30000;

function isDarkColor(hexColor) {
  if (!hexColor || !hexColor.startsWith("#")) return false;
  const hex = hexColor.replace("#", "");
  if (hex.length < 6) return false;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

function extractYear(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  // Try parsing common formats: "22-Apr-24", "05/01/2025", "2025-01-05", etc.
  const s = dateStr.trim();
  // ISO-like: 2025-01-05 or 2025-01-05T...
  const isoMatch = s.match(/^(\d{4})-\d{2}-\d{2}/);
  if (isoMatch) return isoMatch[1];
  // DD-MMM-YY or DD/MMM/YY
  const dMyMatch = s.match(/\d{1,2}[-/]\w{3}[-/](\d{2,4})/);
  if (dMyMatch) {
    const y = dMyMatch[1];
    if (y.length === 4) return y;
    const num = parseInt(y, 10);
    return (num <= 50 ? '20' : '19') + y;
  }
  // DD/MM/YYYY or MM/DD/YYYY
  const slashMatch = s.match(/\d{1,2}[/-]\d{1,2}[/-](\d{4})/);
  if (slashMatch) return slashMatch[1];
  // DD/MM/YY
  const shortSlash = s.match(/\d{1,2}[/-]\d{1,2}[/-](\d{2})$/);
  if (shortSlash) {
    const num = parseInt(shortSlash[1], 10);
    return (num <= 50 ? '20' : '19') + shortSlash[1];
  }
  return '';
}

export default function OneDriveExcel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdminView, setIsAdminView] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeSheet, setActiveSheet] = useState("");
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingTab, setMappingTab] = useState(0);
  const [mappings, setMappings] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [additionalAccess, setAdditionalAccess] = useState([]);
  const [addAccessUser, setAddAccessUser] = useState('');
  const [addAccessMappings, setAddAccessMappings] = useState([]);
  const [addAccessSaving, setAddAccessSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Filters: { colIdx: Set of selected values } — also supports 'year' key for virtual Year col
  const [filters, setFilters] = useState({});
  const [filterAnchor, setFilterAnchor] = useState(null); // { el, colKey }
  const [filterSearch, setFilterSearch] = useState("");
  const lastKnownTimestamp = useRef(null);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('onedrive/api/data-merged/');
      setData(response.data);
      setIsAdminView(response.data.is_admin_view || false);
      setActiveSheet(response.data.active_sheet || "");
      lastKnownTimestamp.current = response.data.last_synced_at;
    } catch (err) {
      if (err.response?.data?.is_admin_view !== undefined) {
        setIsAdminView(err.response.data.is_admin_view);
      }
      setError(err.response?.data?.error || err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll for timestamp changes
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const response = await api.get("onedrive/api/timestamp/");
        const newTimestamp = response.data.last_synced_at;
        if (newTimestamp && newTimestamp !== lastKnownTimestamp.current) {
          lastKnownTimestamp.current = newTimestamp;
          fetchData();
        }
      } catch (err) {
        // Silent fail on polling
      }
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData, activeSheet]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      await api.post("onedrive/api/sync/");
      await fetchData(activeSheet);
    } catch (err) {
      setError(err.response?.data?.error || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const openMappingDialog = async () => {
    setMappingOpen(true);
    setMappingLoading(true);
    try {
      const [mappingsRes, usersRes, accessRes] = await Promise.all([
        api.get("onedrive/api/mappings/"),
        api.get("onedrive/api/users-list/"),
        api.get("onedrive/api/additional-access/"),
      ]);
      setMappings(mappingsRes.data);
      setUsersList(usersRes.data);
      setAdditionalAccess(accessRes.data);
    } catch (err) {
      setError("Failed to load mappings");
    } finally {
      setMappingLoading(false);
    }
  };

  const handleMappingChange = async (mappingId, userIds) => {
    try {
      const response = await api.put(`onedrive/api/mappings/${mappingId}/`, {
        user_ids: userIds,
      });
      setMappings((prev) =>
        prev.map((m) => (m.id === mappingId ? response.data : m))
      );
    } catch (err) {
      setError("Failed to update mapping");
    }
  };

  const handleSaveAdditionalAccess = async () => {
    if (!addAccessUser) return;
    setAddAccessSaving(true);
    try {
      const existing = additionalAccess.find(a => a.user_id === addAccessUser);
      if (existing) {
        await api.put(`onedrive/api/additional-access/${existing.id}/`, { mapping_ids: addAccessMappings });
      } else {
        await api.post('onedrive/api/additional-access/', { user_id: addAccessUser, mapping_ids: addAccessMappings });
      }
      const res = await api.get('onedrive/api/additional-access/');
      setAdditionalAccess(res.data);
      setAddAccessUser('');
      setAddAccessMappings([]);
    } catch (err) {
      setError('Failed to save additional access');
    } finally {
      setAddAccessSaving(false);
    }
  };

  const handleDeleteAdditionalAccess = async (id) => {
    try {
      await api.delete(`onedrive/api/additional-access/${id}/`);
      setAdditionalAccess(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError('Failed to remove access');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      setError(null);
      const formData = new FormData();
      formData.append("file", file);
      await api.post("onedrive/api/upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      await api.delete("onedrive/api/delete/");
      setDeleteConfirmOpen(false);
      setData(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // --- Filtering logic (hooks must be before early returns) ---
  const poDateIdx = data ? data.headers.findIndex(h => h.trim().toLowerCase() === 'po date') : -1;

  const filteredRows = useMemo(() => {
    if (!data || !data.rows) return [];
    const activeFilters = Object.entries(filters).filter(([, vals]) => vals.size > 0);
    if (activeFilters.length === 0) return data.rows;
    return data.rows.filter((row) =>
      activeFilters.every(([colKey, allowed]) => {
        if (colKey === 'year') {
          const dateCell = poDateIdx >= 0 && row.cells[poDateIdx];
          const year = dateCell ? extractYear(String(dateCell.value || '')) : '';
          return allowed.has(year);
        }
        const idx = parseInt(colKey, 10);
        const cellVal = idx < row.cells.length ? String(row.cells[idx].value ?? '') : '';
        return allowed.has(cellVal);
      })
    );
  }, [data, filters, poDateIdx]);

  const handleDownloadExcel = useCallback(async () => {
    if (!data) return;
    try {
      setDownloading(true);

      // Serialize UI filters: convert column-index keys → header names
      const filterPayload = {};
      Object.entries(filters).forEach(([key, valueSet]) => {
        if (!valueSet || valueSet.size === 0) return;
        const values = Array.from(valueSet);
        if (key === 'year') {
          filterPayload['__year__'] = values;
        } else {
          const idx = parseInt(key, 10);
          if (!isNaN(idx) && data.headers[idx] !== undefined) {
            const h = data.headers[idx];
            const name = typeof h === 'object' ? (h.label || h.name || String(h)) : String(h);
            filterPayload[name] = values;
          }
        }
      });

      const response = await api.post(
        'onedrive/api/download-merged/',
        { filters: filterPayload },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const hasFilters = Object.keys(filterPayload).length > 0;
      link.setAttribute('download', hasFilters ? 'PO_Tracker_Filtered.xlsx' : 'PO_Tracker_Merged.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  }, [data, filters]);

  const getUniqueValues = useCallback((colKey) => {
    if (!data || !data.rows) return [];
    const valSet = new Set();
    data.rows.forEach((row) => {
      if (colKey === 'year') {
        const dateCell = poDateIdx >= 0 && row.cells[poDateIdx];
        valSet.add(dateCell ? extractYear(String(dateCell.value || '')) : '');
      } else {
        const idx = parseInt(colKey, 10);
        valSet.add(idx < row.cells.length ? String(row.cells[idx].value ?? '') : '');
      }
    });
    const arr = Array.from(valSet);
    arr.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return arr;
  }, [data, poDateIdx]);

  // --- Early returns (after all hooks) ---
  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchData}>
              RETRY
            </Button>
          }
        >
          {error}
        </Alert>
        {isAdminView && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx,.xls"
              style={{ display: "none" }}
            />
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Upload Excel File"}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  if (!data || !data.rows) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No data available. Upload the Excel file to populate this view.
        </Alert>
        {isAdminView && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".xlsx,.xls"
              style={{ display: "none" }}
            />
            <Button
              variant="contained"
              startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Upload Excel File"}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  const handleSheetChange = async (sheetName) => {
    setActiveSheet(sheetName);
    setFilters({}); // clear filters on sheet change
    if (isAdminView) {
      try {
        await api.post("onedrive/api/set-sheet/", { sheet_name: sheetName });
      } catch (err) {
        // silent fail - sheet still changes locally
      }
    }
    fetchData(sheetName);
  };

  const openFilter = (event, colKey) => {
    setFilterAnchor({ el: event.currentTarget, colKey });
    setFilterSearch("");
  };

  const closeFilter = () => {
    setFilterAnchor(null);
    setFilterSearch("");
  };

  const toggleFilterValue = (colKey, val) => {
    setFilters((prev) => {
      const current = prev[colKey] ? new Set(prev[colKey]) : new Set();
      if (current.has(val)) current.delete(val);
      else current.add(val);
      const next = { ...prev };
      if (current.size === 0) delete next[colKey];
      else next[colKey] = current;
      return next;
    });
  };

  const selectAllFilter = (colKey, values) => {
    setFilters((prev) => ({ ...prev, [colKey]: new Set(values) }));
  };

  const clearColumnFilter = (colKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
  };

  const clearAllFilters = () => setFilters({});

  const activeFilterCount = Object.values(filters).filter(s => s.size > 0).length;

  const unmappedCount = mappings.filter((m) => !m.user_ids || m.user_ids.length === 0).length;

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
        flexWrap="wrap"
        gap={1}
      >
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {data.file_name}
          </Typography>
          {/* Show skipped sheet warning if column mismatch */}
          {data.skipped_sheets && data.skipped_sheets.length > 0 && (
            <Chip
              label={`Skipped (column mismatch): ${data.skipped_sheets.join(', ')}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <Chip
            label={activeFilterCount > 0 ? `${filteredRows.length} / ${data.row_count} rows` : `${data.row_count} rows`}
            size="small"
            color="primary"
            variant="outlined"
          />
          {data.last_synced_at && (
            <Chip
              label={`Synced: ${new Date(data.last_synced_at).toLocaleString()}`}
              size="small"
              variant="outlined"
            />
          )}
          {['faiz@4sight-global.com', 'ram@4sight-global.com'].includes(
            (localStorage.getItem("username") || "").toLowerCase()
          ) && (
            <Tooltip title="View Report">
              <IconButton onClick={() => navigate("/accounts-report")} size="small" color="primary">
                <AssessmentIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchData} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download all sheets merged (with formatting)">
            <IconButton onClick={handleDownloadExcel} size="small" color="success" disabled={!data || downloading}>
              {downloading ? <CircularProgress size={16} color="success" /> : <DownloadIcon />}
            </IconButton>
          </Tooltip>
          {data.is_admin_view && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".xlsx,.xls"
                style={{ display: "none" }}
              />
              <Tooltip title="Upload Excel file">
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  size="small"
                  color="primary"
                >
                  {uploading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <UploadFileIcon />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Sync from source">
                <IconButton
                  onClick={handleSync}
                  disabled={syncing}
                  size="small"
                  color="primary"
                >
                  <SyncIcon className={syncing ? "od-spinning" : ""} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Director Name Mappings">
                <IconButton onClick={openMappingDialog} size="small" color="secondary">
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete file and re-upload">
                <IconButton
                  onClick={() => setDeleteConfirmOpen(true)}
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Active filters bar */}
      {activeFilterCount > 0 && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            Filters ({filteredRows.length} of {data.row_count} rows):
          </Typography>
          {Object.entries(filters).map(([colKey, vals]) => {
            if (vals.size === 0) return null;
            const label = colKey === 'year' ? 'Year' : (data.headers[parseInt(colKey, 10)] || colKey);
            return (
              <Chip
                key={colKey}
                label={`${label}: ${vals.size} selected`}
                size="small"
                onDelete={() => clearColumnFilter(colKey)}
                color="primary"
                variant="outlined"
              />
            );
          })}
          <Chip label="Clear All" size="small" onClick={clearAllFilters} onDelete={clearAllFilters} color="error" variant="outlined" />
        </Stack>
      )}

      {/* Excel-like table */}
      <Paper
        variant="outlined"
        sx={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}
      >
        <table className="od-excel-table">
          <thead>
            <tr>
              <th style={{ minWidth: 40, textAlign: 'center' }}>#</th>
              {data.headers.map((header, idx) => (
                <React.Fragment key={idx}>
                  <th
                    style={{
                      minWidth: data.column_widths[idx]
                        ? `${data.column_widths[idx]}px`
                        : "auto",
                    }}
                  >
                    <span className="od-header-content">
                      {header}
                      <span
                        className={`od-filter-icon${filters[String(idx)] ? ' od-filter-active' : ''}`}
                        onClick={(e) => openFilter(e, String(idx))}
                      >
                        &#9660;
                      </span>
                    </span>
                  </th>
                  {idx === poDateIdx && (
                    <th style={{ minWidth: 60 }}>
                      <span className="od-header-content">
                        Year
                        <span
                          className={`od-filter-icon${filters['year'] ? ' od-filter-active' : ''}`}
                          onClick={(e) => openFilter(e, 'year')}
                        >
                          &#9660;
                        </span>
                      </span>
                    </th>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                <td style={{ textAlign: 'center', color: '#888', fontSize: 12 }}>{rowIdx + 1}</td>
                {row.cells.map((cell, cellIdx) => (
                  <React.Fragment key={cellIdx}>
                    <td
                      title={cell.value != null && cell.value !== '' ? String(cell.value) : undefined}
                      style={{
                        textAlign: typeof cell.value === 'number' || (typeof cell.value === 'string' && /^-?[\d,]+(\.\d+)?%?$/.test(cell.value.trim())) ? 'right' : undefined,
                        backgroundColor: cell.bg_color || "transparent",
                        color: cell.font_color && isDarkColor(cell.font_color)
                          ? cell.font_color
                          : cell.bg_color && isDarkColor(cell.bg_color)
                            ? "#FFFFFF"
                            : "#000000",
                      }}
                    >
                      {cell.value}
                    </td>
                    {cellIdx === poDateIdx && (
                      <td style={{ textAlign: 'center' }}>
                        {extractYear(String(cell.value || ''))}
                      </td>
                    )}
                  </React.Fragment>
                ))}
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={data.headers.length + (poDateIdx >= 0 ? 2 : 1)}
                  style={{ textAlign: "center", padding: "40px", color: "#888" }}
                >
                  {activeFilterCount > 0 ? 'No rows match the current filters.' : 'No data visible for your account.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Paper>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor?.el}
        onClose={closeFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {filterAnchor && (() => {
          const colKey = filterAnchor.colKey;
          const allValues = getUniqueValues(colKey);
          const selected = filters[colKey] || new Set();
          const filtered = filterSearch
            ? allValues.filter(v => v.toLowerCase().includes(filterSearch.toLowerCase()))
            : allValues;
          return (
            <Box sx={{ width: 280, maxHeight: 400, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 1, borderBottom: '1px solid #eee' }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  fullWidth
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  autoFocus
                />
              </Box>
              <Box sx={{ px: 1, py: 0.5, borderBottom: '1px solid #eee', display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => selectAllFilter(colKey, filtered)}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={() => clearColumnFilter(colKey)}
                >
                  Clear
                </Button>
              </Box>
              <Box sx={{ overflow: 'auto', maxHeight: 280, p: 0.5 }}>
                {filtered.map((val) => (
                  <FormControlLabel
                    key={val}
                    sx={{ display: 'flex', mx: 0, '& .MuiTypography-root': { fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 } }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.has(val)}
                        onChange={() => toggleFilterValue(colKey, val)}
                      />
                    }
                    label={val || '(Empty)'}
                    title={val || '(Empty)'}
                  />
                ))}
                {filtered.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1, textAlign: 'center' }}>
                    No matches
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })()}
      </Popover>

      {/* Director Name Mapping Modal (Admin only) */}
      {mappingOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 800, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', fontFamily: 'inherit' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '24px 24px 20px', flexShrink: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/><path d="M22 12A10 10 0 0 0 12 2v4a6 6 0 0 1 6 6z"/></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Access Settings</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>Manage director name mappings and user access</div>
            </div>
            <button onClick={() => setMappingOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 2, lineHeight: 1, marginTop: 2 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <Tabs value={mappingTab} onChange={(_, v) => setMappingTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
            <Tab label="Director Mappings" />
            <Tab label="Additional Access" />
          </Tabs>
          <div style={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
          {mappingLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : mappingTab === 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Map each Excel director name to a PMS user. Unmapped names will
                only be visible to admins.
              </Typography>
              {unmappedCount > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {unmappedCount} unmapped director name(s). These rows are only
                  visible to admins until mapped.
                </Alert>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Excel Director Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Mapped PMS User</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappings.map((mapping) => (
                      <TableRow key={mapping.id}>
                        <TableCell sx={{ fontWeight: 600 }}>{mapping.excel_name}</TableCell>
                        <TableCell>
                          {mapping.user_ids && mapping.user_ids.length > 0 ? (
                            <Chip icon={<CheckCircleIcon />} label={`${mapping.user_ids.length} mapped`} color="success" size="small" variant="outlined" />
                          ) : (
                            <Chip icon={<WarningIcon />} label="Unmapped" color="warning" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 280 }}>
                            <Select
                              multiple
                              value={mapping.user_ids || []}
                              onChange={(e) => handleMappingChange(mapping.id, e.target.value)}
                              displayEmpty
                              MenuProps={{ style: { zIndex: 10001 } }}
                              renderValue={(selected) => {
                                if (!selected || selected.length === 0) return <em>-- Select PMS Users --</em>;
                                return selected.map((id) => { const u = usersList.find((u) => u.id === id); return u ? u.name : id; }).join(", ");
                              }}
                            >
                              {usersList.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                  {user.name} ({user.email}){user.designation && ` - ${user.designation}`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    ))}
                    {mappings.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: "center", py: 3 }}>
                          No director names found. Sync the Excel file first.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Grant a user access to specific directors' rows, in addition to their normal hierarchy access.
              </Typography>

              {/* Add / Edit grant form */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>User</InputLabel>
                  <Select
                    label="User"
                    value={addAccessUser}
                    MenuProps={{ style: { zIndex: 10001 } }}
                    onChange={(e) => {
                      setAddAccessUser(e.target.value);
                      const existing = additionalAccess.find(a => a.user_id === e.target.value);
                      setAddAccessMappings(existing ? existing.director_mappings.map(m => m.id) : []);
                    }}
                  >
                    {usersList.map(u => (
                      <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                  <InputLabel>Directors to grant access to</InputLabel>
                  <Select
                    multiple
                    label="Directors to grant access to"
                    value={addAccessMappings}
                    onChange={(e) => setAddAccessMappings(e.target.value)}
                    disabled={!addAccessUser}
                    MenuProps={{ style: { zIndex: 10001 } }}
                    renderValue={(selected) => selected.map(id => { const m = mappings.find(m => m.id === id); return m ? m.excel_name : id; }).join(', ')}
                  >
                    {mappings.map(m => (
                      <MenuItem key={m.id} value={m.id}>{m.excel_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveAdditionalAccess}
                  disabled={!addAccessUser || addAccessMappings.length === 0 || addAccessSaving}
                  sx={{ mt: 0.5 }}
                >
                  {addAccessSaving ? <CircularProgress size={18} /> : 'Save'}
                </Button>
              </Box>

              {/* Existing grants table */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Has access to</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {additionalAccess.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          No additional access grants yet.
                        </TableCell>
                      </TableRow>
                    ) : additionalAccess.map(grant => (
                      <TableRow key={grant.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{grant.user_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{grant.user_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {grant.director_mappings.map(m => (
                              <Chip key={m.id} label={m.excel_name} size="small" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="primary" onClick={() => {
                            setAddAccessUser(grant.user_id);
                            setAddAccessMappings(grant.director_mappings.map(m => m.id));
                          }} title="Edit">
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteAdditionalAccess(grant.id)} title="Remove">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          </div>
        </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', fontFamily: 'inherit', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '24px 24px 20px' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <DeleteIcon sx={{ fontSize: 20, color: '#dc2626' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Delete Excel File?</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 3, lineHeight: 1.5 }}>This will remove the current file data and all director name mappings. You can upload a new file afterwards.</div>
              </div>
              <button onClick={() => setDeleteConfirmOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 2, lineHeight: 1, marginTop: 2 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={{ borderTop: '1px solid #f1f5f9', background: '#f8fafc', padding: '14px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}
                style={{ padding: '9px 18px', border: 'none', borderRadius: 9, background: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: '#64748b', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ padding: '9px 20px', border: 'none', borderRadius: 9, background: deleting ? '#f1f5f9' : '#dc2626', color: deleting ? '#94a3b8' : '#fff', cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
                {deleting ? <CircularProgress size={14} sx={{ color: '#94a3b8' }} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}
